/**
 * Shared utilities for content feed forms
 */

import { getEmailAlias } from "utils/ContentFeedCreation";

export const GOOGLE_SPREADSHEET_SOURCE_LABEL = "Google Spreadsheet";

/**
 * Assign flow: rebuild docs.google.com spreadsheet URL as …/d/{id}/edit?gid={selectedSheet}.
 * Extracts spreadsheet id from the path; ignores query/hash on the original URL.
 * Call only when the user has chosen a worksheet (`sheetName` from the form).
 * Other URLs are returned unchanged.
 */
export function buildGoogleSpreadsheetAssignSourceUrl(sourceUrl, sheetNameField) {
	const trimmed = (sourceUrl || "").trim();
	const matched = trimmed.match(/^(https?):\/\/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/i);
	if (!matched) {
		return trimmed;
	}

	const gid = String(sheetNameField.value);
	return `${matched[1].toLowerCase()}://docs.google.com/spreadsheets/d/${matched[2]}/edit?gid=${gid}`;
}

/**
 * Builds arguments for assigning existing content feed
 * @param {Object} formValues - The form values object
 * @param {Array} creativeIds - Array of creative IDs
 * @param {string} libraryId - The library ID
 * @returns {Object} API call arguments
 */
export function buildAssignContentFeedArgs(formValues, creativeIds, libraryId) {
	let sourceUrl = formValues?.sourceUrl?.trim();
	if (formValues?.sourceType?.label === GOOGLE_SPREADSHEET_SOURCE_LABEL && sourceUrl && formValues?.sheetName) {
		sourceUrl = buildGoogleSpreadsheetAssignSourceUrl(sourceUrl, formValues.sheetName);
	}
	return {
		sourceUrl,
		sourceType: formValues?.sourceType?.label,
		email: getEmailAlias(formValues?.feedEmails),
		feedName: formValues?.feedName,
		creativeIds,
		creativeLibraryId: String(libraryId),
	};
}

/**
 * Builds arguments for assigning blank content feed
 * @param {Object} formValues - The form values object
 * @param {Array} creativeIds - Array of creative IDs
 * @param {string} libraryId - The library ID
 * @returns {Object} API call arguments
 */
export function buildAssignBlankArgs(formValues, creativeIds, libraryId) {
	return {
		feedName: formValues?.feedName,
		email: getEmailAlias(formValues?.feedEmails),
		sourceType: formValues?.sourceType?.label,
		creativeIds,
		creativeLibraryId: String(libraryId),
	};
}

/**
 * Creates navigation functions for content feed forms
 * @param {Object} history - React Router history object
 * @param {string} libraryId - The library ID
 * @returns {Object} Navigation functions
 */
export function createContentFeedNavigation(history, libraryId) {
	const goBackToFeedsList = () => history.push(`/library/${libraryId}/content-feeds/`);
	const goBackToCreativesList = () => history.push(`/library/${libraryId}/`);

	const dispatchWithFeedsListRedirect = actionPromise =>
		actionPromise.then((response) => {
			if (response?.success === true) {
				goBackToFeedsList();
			}
			return response;
		});

	return {
		goBackToFeedsList,
		goBackToCreativesList,
		dispatchWithFeedsListRedirect,
	};
}

/**
 * Default form values for assign blank flow
 */
export const ASSIGN_BLANK_DEFAULT_VALUES = {
	autoMapping: true,
	autoUpdates: true,
	sourceUrl: 'www.innovidfeed.com',
	sourceType: { label: "Google Spreadsheet", value: "google-spreadsheet" },
};
